import { createAxiosInstanceSetAction } from "./../redux/actions/axios";
import {
  DataProvider,
  GetListParams,
  GetOneParams,
  GetManyParams,
  GetManyReferenceParams,
  UpdateParams,
  UpdateManyParams,
  CreateParams,
  DeleteParams,
  DeleteManyParams,
  GetListResult,
  GetOneResult,
  GetManyResult,
  GetManyReferenceResult,
  UpdateResult,
  UpdateManyResult,
  CreateResult,
  DeleteResult,
  DeleteManyResult,
  Record,
} from "react-admin";
import { Configuration, RecordStatus } from "./service-proxies";
import { AuthManager } from "../auth/Auth";
import Axios, {
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { serviceProxyFactory } from "./serviceProxyFactory";
import resourceMap from "./resourceMap";
import {
  IResourceMap,
  IResolvedApiResource,
  ChangeStatusParams,
} from "./types";
import apiToDataProviderMap from "./apiToDataProviderMap";
import dataProviderToApiMap from "./dataProviderToApiMap";
import messages from "../i18n/messages";
import { Dispatch } from "react";
import { handleAxiosError } from "./axiosErrorHandler";
import { exclusiveMasterResources } from "./exclusiveMasterResources";
import { showServerMaintenanceDialog } from "../store/serverMaintenanceStore";

interface ExportParams {
  sort?: GetListParams["sort"];
  filter?: GetListParams["filter"];
  pagination?: GetListParams["pagination"];
  exportFormat?: string;
  fileName?: string;
}

interface OidcRetryConfig extends AxiosRequestConfig {
  oidcSessionRecoveryAttempted?: boolean;
}

/**
 * The logout detail rides to identity on a URL that already carries an id token,
 * so send the path only - a query string can carry ids that have no business
 * being in a redirect.
 */
const pathOf = (url?: string) => (url ?? "-").split("?")[0];

export class ApiManager implements DataProvider {
  private authManager: AuthManager;
  private resourceMap?: IResourceMap;
  private axiosInstance?: AxiosInstance;
  private baseURL: string;
  private searchFields: { [key: string]: Array<string> };

  constructor(authManager: AuthManager, baseURL: string) {
    this.baseURL = baseURL;
    this.authManager = authManager;
    this.searchFields = {};
  }

  [key: string]: any;

  init = (dispatch: Dispatch<any>) => {
    this.axiosInstance = Axios.create({
      baseURL: this.baseURL,
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          config.headers = {
            ...config.headers,
            ...config.headers.common,
            Authorization:
              config.headers.Authorization ??
              `Bearer ${await this.authManager.getAccessToken()}`,
            "X-Client-TimeZone": Intl.DateTimeFormat().resolvedOptions()
              .timeZone,
          };
        } catch (e) {
          console.warn("Unable to obtain an access token");
        }
        return config;
      },
      (error) => {
        console.error(error);
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        if (response.headers["x-session-logout"]) {
          this.authManager.logoutFromIdentityProvider({
            redirectBack: true,
            reason: "x-session-logout",
            detail: `${response.status} ${pathOf(response.config?.url)}`,
          });
        }

        return response;
      },
      async (error) => {
        const code = error?.response?.data?.error?.code;
        const message =
          error?.response?.data?.error?.message ??
          "The server is undergoing maintenance. Please try again later.";
        if (code === "ServerMaintenance") {
          showServerMaintenanceDialog(message);
        }

        const requestConfig = error?.config as OidcRetryConfig | undefined;
        if (
          error?.response?.status === 401 &&
          requestConfig &&
          this.axiosInstance
        ) {
          if (requestConfig.oidcSessionRecoveryAttempted) {
            await this.authManager.logoutAfterUnauthorized(
              "api-401-after-recovery",
              `401 ${pathOf(requestConfig.url)}`
            );
            return Promise.reject(error);
          }

          requestConfig.oidcSessionRecoveryAttempted = true;
          const requestHeaders = AxiosHeaders.from(requestConfig.headers);
          const authorization = requestHeaders.get("Authorization");
          const rejectedAccessToken =
            typeof authorization === "string" &&
            authorization.startsWith("Bearer ")
              ? authorization.substring("Bearer ".length)
              : undefined;

          try {
            const renewedAccessToken = this.authManager.isUserOverride()
              ? await this.authManager.recoverUserOverride(rejectedAccessToken)
              : await this.authManager.recoverOidcSession(rejectedAccessToken);
            requestHeaders.set("Authorization", `Bearer ${renewedAccessToken}`);
            requestConfig.headers = requestHeaders;
            return await this.axiosInstance.request(requestConfig);
          } catch {
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    // If already authenticated, dispatch the AXIOS_INSTANCE_SET action,
    // else schedule the dispatch when the user is authenticated
    const dispatchAxiosInstanceSetWhenReady = (
      axios: AxiosInstance,
      baseURL: string
    ) => {
      this.authManager
        .checkAuth(null)
        .then(() => {
          dispatch(
            createAxiosInstanceSetAction({ axios: axios, baseURL: baseURL })
          );
        })
        .catch(() => {
          this.authManager.addUserLoadedEvent(() => {
            dispatch(
              createAxiosInstanceSetAction({ axios: axios, baseURL: baseURL })
            );
          });
        });
    };
    dispatchAxiosInstanceSetWhenReady(this.axiosInstance, this.baseURL);
    this.resourceMap = resourceMap(this.baseURL, this.axiosInstance);
  };

  getResourceCrudInterface = (
    resource: any,
    config?: Configuration
  ): IResolvedApiResource | null => {
    if (!this.resourceMap) {
      return null;
    }

    const apiResource = this.resourceMap.getApiResource(resource);
    if (!apiResource) {
      throw new Error(
        `The resource map doesn't contain the requested resource '${resource}'`
      );
    }

    return {
      api: apiResource.api(config),
      apiRouteName: apiResource.apiRouteName,
      isMockResource: apiResource.isMockResource,
    };
  };

  getList = async <RecordType extends Record = Record>(
    resource: string,
    params: GetListParams,
    config?: Configuration,
    options?: any
  ): Promise<GetListResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const apiParams = dataProviderToApiMap.mapGetListParams(
        params,
        this.searchFields[resource]
      );

      // Make sure the original access token is used for exclusive master resources get list calls
      if (exclusiveMasterResources.includes(resource)) {
        options = {
          headers: {
            Authorization: `Bearer ${await this.authManager.getAccessToken({
              ignoreUserOverride: true,
            })}`,
            ...options,
          },
        };
      }

      const result = proxy.getFilteredList(apiParams, options);
      return await apiToDataProviderMap.mapToListResult(result);
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  exportResource = async (
    resource: string,
    params: ExportParams,
    config?: Configuration,
    options?: any
  ): Promise<void> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      if (!proxy.exportData) {
        throw new Error("Export is not available for this resource");
      }
      const pagination: GetListParams["pagination"] = params.pagination ?? {
        page: 1,
        perPage: 1000,
      };

      const apiParams = dataProviderToApiMap.mapExportParams(
        {
          sort: params.sort,
          filter: params.filter ?? {},
          pagination,
          exportFormat: params.exportFormat,
        },
        this.searchFields[resource]
      );

      const baseOptions = {
        responseType: "blob",
        ...(options ?? {}),
      };

      const exportOptions = exclusiveMasterResources.includes(resource)
        ? {
            ...baseOptions,
            headers: {
              Authorization: `Bearer ${await this.authManager.getAccessToken({
                ignoreUserOverride: true,
              })}`,
            },
          }
        : baseOptions;

      const result = await proxy.exportData(apiParams, exportOptions);
      this.saveBlobResponse(
        result,
        `${resource}-export.${params.exportFormat ?? "export-data"}`
      );
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  getOne = async <RecordType extends Record = Record>(
    resource: string,
    params: GetOneParams,
    config?: Configuration
  ): Promise<GetOneResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.getOneById(`${params.id}`);
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  getMany = async <RecordType extends Record = Record>(
    resource: string,
    params: GetManyParams,
    config?: Configuration
  ): Promise<GetManyResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.getMany(params.ids.map((id) => `${id}`));
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  getManyReference = async <RecordType extends Record = Record>(
    resource: string,
    params: GetManyReferenceParams,
    config?: Configuration
  ): Promise<GetManyReferenceResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const apiParams = dataProviderToApiMap.mapGetManyReferenceParams(params);
      const result = proxy.getFilteredList(apiParams);
      return await apiToDataProviderMap.mapToListResult(result);
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  update = async <RecordType extends Record = Record>(
    resource: string,
    params: UpdateParams,
    config?: Configuration
  ): Promise<UpdateResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.updateOne(`${params.id}`, params.data);
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  updateMany = async (
    resource: string,
    params: UpdateManyParams,
    config?: Configuration
  ): Promise<UpdateManyResult> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.updateMany({
        ids: params.ids.map((id) => `${id}`),
        data: params.data,
      });
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  create = async <RecordType extends Record = Record>(
    resource: string,
    params: CreateParams,
    config?: Configuration
  ): Promise<CreateResult<RecordType>> => {
    if (params.data.__endpointOverride && this.axiosInstance) {
      const { __endpointOverride, ...data } = params.data;
      return await this.axiosInstance.post(__endpointOverride, data);
    }

    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.createOne(params.data);
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  delete = async <RecordType extends Record = Record>(
    resource: string,
    params: DeleteParams,
    config?: Configuration
  ): Promise<DeleteResult<RecordType>> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      await proxy.deleteOne(`${params.id}`);
      return { data: params.previousData as RecordType };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  deleteMany = async (
    resource: string,
    params: DeleteManyParams,
    config?: Configuration
  ): Promise<DeleteManyResult> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      const result = await proxy.deleteMany(params.ids.map((id) => `${id}`));
      return { data: result.data };
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  changeStatus = async (
    resource: string,
    params: ChangeStatusParams,
    config?: Configuration
  ): Promise<Record & { status: RecordStatus }> => {
    const api = this.getApi(resource, config);
    if (!api) {
      throw new Error(messages.INTERNAL_ERROR);
    }

    try {
      const proxy = serviceProxyFactory(api);
      if (!proxy.changeStatus) {
        return Promise.reject(
          "This entity doesn't support the changeStatus method"
        );
      }
      const result = await proxy.changeStatus(params.id, params.status);
      return result.data;
    } catch (error) {
      const code = handleAxiosError(error);
      throw new Error(code);
    }
  };

  setSearchFields = (resource: string, fields: Array<string>) => {
    this.searchFields[resource] = [...fields];
  };

  private saveBlobResponse = (
    response: AxiosResponse<Blob>,
    fallbackFileName: string
  ) => {
    if (!response || !response.data) {
      return;
    }
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fallbackFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  private getApi = (
    resource: string,
    config?: Configuration
  ): IResolvedApiResource | null => {
    let api: IResolvedApiResource | null = null;
    try {
      api = this.getResourceCrudInterface(resource, config);
      if (!api) {
        throw new Error(messages.API_CALL_INIT_FIRST);
      }
    } catch (error) {
      console.error(error);
    }

    return api;
  };
}
